import type {Client} from "typesense";

export type TypesenseSearchCluster = "cloud" | "internal";
export type TypesenseSearchCollectionGroup = "projects" | "parts" | "unknown";
export type TypesenseSearchErrorCategory =
    | "none"
    | "timeout"
    | "aborted"
    | "auth"
    | "rate-limited"
    | "server"
    | "network"
    | "unknown";

export interface TypesenseSearchMeasurement {
    cluster: TypesenseSearchCluster;
    collectionGroup: TypesenseSearchCollectionGroup;
    surface: "direct";
    startedAt: number;
    durationMs: number;
    success: boolean;
    timedOut: boolean;
    errorCategory: TypesenseSearchErrorCategory;
    resultCount?: number;
    routeVariant: TypesenseSearchCluster;
}

export type TypesenseSearchRecorder = (measurement: TypesenseSearchMeasurement) => void;

interface SearchResponse {
    found?: unknown;
    found_docs?: unknown;
    hits?: unknown;
}

function resultCount(response: unknown): number | undefined {
    if (!response || typeof response !== "object") return undefined;
    const searchResponse = response as SearchResponse;
    if (typeof searchResponse.found === "number") return searchResponse.found;
    if (typeof searchResponse.found_docs === "number") return searchResponse.found_docs;
    if (Array.isArray(searchResponse.hits)) return searchResponse.hits.length;
    return undefined;
}

function classifyError(error: unknown): {category: TypesenseSearchErrorCategory; timedOut: boolean} {
    if (!error || typeof error !== "object") {
        return {category: "unknown", timedOut: false};
    }

    const fields = error as {name?: unknown; message?: unknown; code?: unknown; httpStatus?: unknown};
    const name = typeof fields.name === "string" ? fields.name.toLowerCase() : "";
    const message = typeof fields.message === "string" ? fields.message.toLowerCase() : "";
    const code = typeof fields.code === "string" ? fields.code.toLowerCase() : "";
    const status = typeof fields.httpStatus === "number" ? fields.httpStatus : undefined;

    if (name === "aborterror" || code === "err_canceled") return {category: "aborted", timedOut: false};
    if (code.includes("timeout") || message.includes("timeout") || message.includes("timed out")) {
        return {category: "timeout", timedOut: true};
    }
    if (status === 401 || status === 403) return {category: "auth", timedOut: false};
    if (status === 429) return {category: "rate-limited", timedOut: false};
    if (status !== undefined && status >= 500) return {category: "server", timedOut: false};
    if (code.startsWith("econn") || code === "network_error" || name === "typeerror") {
        return {category: "network", timedOut: false};
    }
    return {category: "unknown", timedOut: false};
}

function recordSearch(record: TypesenseSearchRecorder, measurement: TypesenseSearchMeasurement): void {
    try {
        record(measurement);
    } catch {
        return;
    }
}

async function measureSearch<T>(
    record: TypesenseSearchRecorder,
    cluster: TypesenseSearchCluster,
    group: TypesenseSearchCollectionGroup,
    search: () => Promise<T>,
): Promise<T> {
    const startedAt = performance.now();
    try {
        const response = await search();
        recordSearch(record, {
            cluster,
            collectionGroup: group,
            surface: "direct",
            startedAt,
            durationMs: performance.now() - startedAt,
            success: true,
            timedOut: false,
            errorCategory: "none",
            resultCount: resultCount(response),
            routeVariant: cluster,
        });
        return response;
    } catch (error) {
        const {category, timedOut} = classifyError(error);
        recordSearch(record, {
            cluster,
            collectionGroup: group,
            surface: "direct",
            startedAt,
            durationMs: performance.now() - startedAt,
            success: false,
            timedOut,
            errorCategory: category,
            routeVariant: cluster,
        });
        throw error;
    }
}

export function instrumentTypesenseSearches(
    client: Client,
    cluster: TypesenseSearchCluster,
    group: TypesenseSearchCollectionGroup,
    record: TypesenseSearchRecorder,
): Client {
    const originalGet = client.apiCall.get.bind(client.apiCall);
    client.apiCall.get = (<T>(endpoint: string, ...args: unknown[]) => {
        const match = /^\/collections\/([^/]+)\/documents\/search$/.exec(endpoint);
        if (!match) return Reflect.apply(originalGet, client.apiCall, [endpoint, ...args]) as Promise<T>;

        return measureSearch(record, cluster, group, () =>
            Reflect.apply(originalGet, client.apiCall, [endpoint, ...args]),
        ) as Promise<T>;
    }) as typeof client.apiCall.get;

    return client;
}
